/**
 * Client HTTP dell'applicazione. È l'UNICO punto da cui partono chiamate all'API.
 *
 * Responsabilità:
 *  - conserva l'access token IN MEMORIA (mai in localStorage: un XSS non deve
 *    poterlo leggere — ADR-0004);
 *  - su 401 tenta un refresh silenzioso e ripete la richiesta una sola volta;
 *  - se più richieste falliscono insieme, il refresh parte UNA volta e le altre
 *    si mettono in coda sulla stessa promise;
 *  - normalizza gli errori del backend in `ApiError`.
 */

const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * URL assoluto di una risorsa che il browser scarica da sé (un `<img>`), cioè
 * senza passare da `apiRequest` e quindi senza token: solo per rotte pubbliche.
 */
export function apiResourceUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export type ApiErrorDetail = {
  path: string;
  message: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetail[];
  readonly requestId: string | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    details: ApiErrorDetail[] = [],
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** `true` quando l'errore è imputabile all'input dell'utente, non al sistema. */
  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR';
  }

  /**
   * `true` quando a rispondere è stato un **intermediario**, non l'applicazione:
   * il proxy ha rinunciato ad attendere, o non ha trovato il backend.
   *
   * Il discriminante è `code === 'UNKNOWN'`, cioè un corpo che non è
   * l'involucro `{ error: { code, message } }` del backend: Nginx risponde con
   * la propria pagina HTML. Senza questa distinzione, un 504 del proxy arriva
   * all'utente come «Errore 504» e si legge come un fallimento definitivo —
   * mentre il lavoro dall'altra parte è quasi sempre ancora in corso, e spesso
   * si conclude bene.
   */
  get isGatewayError(): boolean {
    return this.code === 'UNKNOWN' && [502, 503, 504].includes(this.status);
  }
}

// -----------------------------------------------------------------------------
// Stato del token (in memoria)
// -----------------------------------------------------------------------------

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registra il callback invocato quando la sessione non è più recuperabile. */
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

// -----------------------------------------------------------------------------
// Refresh con coda
// -----------------------------------------------------------------------------

/**
 * Promise del refresh in corso. Serve a evitare che N richieste fallite in
 * parallelo scatenino N refresh: il primo vince, gli altri lo attendono.
 * Fondamentale perché il refresh token viene RUOTATO a ogni uso — due refresh
 * concorrenti farebbero scattare il rilevamento di riuso lato server.
 */
let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // il refresh token viaggia nel cookie httpOnly
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return data.accessToken;
  } catch {
    return null;
  }
}

function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/** Tenta di ripristinare la sessione all'avvio dell'app, senza token in memoria. */
export async function restoreSession(): Promise<boolean> {
  const token = await refreshAccessToken();
  return token !== null;
}

// -----------------------------------------------------------------------------
// Richieste
// -----------------------------------------------------------------------------

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Corpo multipart per gli upload di file. Alternativo a `body`: il
   * `Content-Type` NON va impostato a mano, perché il browser deve aggiungerci
   * il `boundary` generato per quella specifica richiesta.
   */
  formData?: FormData;
  signal?: AbortSignal;
  /** Disattiva il refresh automatico: usato dagli endpoint di auth stessi. */
  skipAuthRetry?: boolean;
  /** `text` per le risposte non JSON (es. export CSV). Default: JSON. */
  parse?: 'json' | 'text';
};

async function parseError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN';
  let message = `Errore ${response.status}`;
  let details: ApiErrorDetail[] = [];
  let requestId: string | undefined;

  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: ApiErrorDetail[]; requestId?: string };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details ?? [];
      requestId = body.error.requestId;
    }
  } catch {
    // Risposta non JSON (es. errore del proxy): si tiene il messaggio generico.
  }

  return new ApiError(response.status, code, message, details, requestId);
}

async function executeRequest(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  // Con FormData il Content-Type lo scrive il browser, boundary compreso:
  // impostarlo qui produrrebbe un multipart che il server non sa spacchettare.
  if (options.body !== undefined && !options.formData) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const body = options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined);

  return fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await executeRequest(path, options);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', 'Impossibile contattare il server');
  }

  // Un 401 può voler dire "access token scaduto": si tenta il refresh una volta.
  if (response.status === 401 && !options.skipAuthRetry) {
    const newToken = await refreshAccessToken();

    if (newToken === null) {
      accessToken = null;
      onSessionExpired?.();
      throw await parseError(response);
    }

    response = await executeRequest(path, options);
  }

  if (!response.ok) throw await parseError(response);

  // 204 No Content non ha corpo: parsarlo genererebbe un errore.
  if (response.status === 204) return undefined as T;

  if (options.parse === 'text') return (await response.text()) as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(path, signal ? { signal } : {}),
  /** Risposta testuale (es. CSV): stesso refresh su 401 delle altre chiamate. */
  getText: (path: string) => apiRequest<string>(path, { parse: 'text' }),
  post: <T>(path: string, body?: unknown, options?: Partial<RequestOptions>) =>
    apiRequest<T>(path, { method: 'POST', body, ...options }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
  /** Upload multipart. Il refresh automatico su 401 funziona come per le altre chiamate. */
  upload: <T>(path: string, formData: FormData) =>
    apiRequest<T>(path, { method: 'POST', formData }),
};
